import { useEffect, useRef, useState } from "react";
import axios from "../api/axios";

const HEALTHY_INTERVAL_MS = 30000;
const UNHEALTHY_INTERVAL_MS = 5000;

/**
 * Polls GET /health and reports whether the backend is reachable.
 * Polls less often while healthy, more often while down, to detect
 * recovery quickly without hammering the server in the steady state.
 *
 * @param {Object} [options]
 * @param {Function} [options.onRecovered] - called once when the backend
 *   transitions from down to up (e.g. to trigger syncManager.catchUp()).
 */
export const useBackendHealth = ({ onRecovered } = {}) => {
  const [isBackendUp, setIsBackendUp] = useState(true);
  const wasDownRef = useRef(false);
  const onRecoveredRef = useRef(onRecovered);
  onRecoveredRef.current = onRecovered;

  useEffect(() => {
    let cancelled = false;
    let timeoutId;

    const check = async () => {
      let up = true;
      try {
        await axios.get("/health", { timeout: 4000 });
      } catch {
        up = false;
      }

      if (!cancelled) {
        setIsBackendUp(up);
        if (up && wasDownRef.current) {
          wasDownRef.current = false;
          onRecoveredRef.current?.();
        } else if (!up) {
          wasDownRef.current = true;
        }
        timeoutId = setTimeout(check, up ? HEALTHY_INTERVAL_MS : UNHEALTHY_INTERVAL_MS);
      }
    };

    check();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  return { isBackendUp };
};
